<template>
  <div>
    <div v-if="!questionsStore.question" class="py-10 flex justify-center">
      <BaseSpinner />
    </div>
    <div v-else class="flex flex-col gap-6">
      <article
        class="p-4 sm:p-8 bg-white rounded-xl text-gray-900 flex flex-col gap-4"
      >
        <div class="w-full flex justify-between gap-4">
          <div class="flex items-center gap-4 flex-wrap">
            <div
              v-if="categories.length"
              class="flex flex-wrap items-center gap-1"
            >
              <BaseLocaleLink
                v-for="item in categories"
                :key="item.id"
                :to="item.route"
                class="flex no-underline font-normal"
                :class="item.hidden && 'pointer-events-none'"
              >
                <BaseButton
                  size="xs"
                  look="none"
                  class="bg-primary-100 hover:bg-primary-400"
                >
                  {{ item.local }}
                </BaseButton>
              </BaseLocaleLink>
            </div>
            <span class="text-xs sm:text-sm whitespace-nowrap text-gray-400">
              {{
                questionsStore.formatDate(questionsStore.question.updated_at)
              }}
            </span>
          </div>
          <BaseButton
            look="link"
            theme="dark"
            class="font-normal hidden sm:inline-flex"
            size="xs"
            @click="goBackToQuestions"
          >
            <BaseIcon name="outline_arrow_turn_left" size="xs" class="mr-2" />
            <span class="text-sm">
              {{ $t('questions.back_to_questions') }}
            </span>
          </BaseButton>
        </div>
        <div class="flex py-1 justify-between gap-4">
          <div class="flex gap-2">
            <div>
              <UsersUserAvatar
                :src="questionsStore.question.user?.media"
                :abbr="
                  getAbbrFromFullName(questionsStore.question.user?.name || '')
                "
                size="lg"
              >
                <template
                  v-if="questionsStore.question.user.is_subscription_active"
                  #topBadge
                >
                  <SubscriptionsProBadge />
                </template>
              </UsersUserAvatar>
            </div>
            <div class="font-bold text-sm text-gray flex gap-2 items-center">
              <BaseLocaleLink
                v-if="
                  authStore.auth && !questionsStore.question.user.is_removed
                "
                :to="`/users/${questionsStore.question.sender_id}`"
                class="hover:text-gray-700"
              >
                {{ questionsStore.question.user.name }}
              </BaseLocaleLink>
              <span
                v-else
                :class="questionsStore.question.user.is_removed && 'opacity-60'"
              >
                {{ questionsStore.question.user.name }}
              </span>
              <span class="font-normal text-gray-400 whitespace-nowrap">
                ·
                {{
                  questionsStore.formatDateFrom(
                    questionsStore.question.created_at
                  )
                }}
              </span>
            </div>
          </div>
          <QuestionsMenu
            :question="questionsStore.question"
            :page-link="pageLink"
          />
        </div>
        <h2 class="text-3xl font-bold">
          {{ questionsStore.question.title }}
        </h2>
        <div class="pb-2 whitespace-pre-wrap">
          {{ questionsStore.question.text }}
        </div>
        <div class="pt-5 border-t border-gray-200">
          <CardActionsPanel
            :name="questionsStore.question.title"
            :reactions="reactionsCounters"
            :comments="questionsStore.question.count_answers"
            :page-link="pageLink"
            :is-counters-processing="isCountersProcessing"
            @change-reaction="onChangeReaction"
          />
        </div>
      </article>
      <div
        ref="answersElement"
        class="p-4 sm:p-8 bg-white rounded-lg"
        style="scroll-margin-top: calc(4rem + var(--sat))"
      >
        <AnswersList
          :question-id="id"
          @loaded="scrollToAnswers"
          @create="onCreateAnswer"
          @delete="onDeleteAnswer"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router'
import { useQuestions } from '~/stores/questions'
import { EntityType } from '~/models/common'
import { useNavigation } from '~/stores/navigation'
import { useAuth } from '~/stores/auth'

const nuxtApp = useNuxtApp()
const questionsStore = useQuestions()
const route = useRoute()
const router = useRouter()
const { getCategoryItemsByIds, getPageLinkCategorySlug } = useNavigation()
const authStore = useAuth()
const answersElement = ref<HTMLElement | null>(null)

const id = Array.isArray(route.params.id)
  ? Number(route.params.id[0])
  : Number(route.params.id)

const categories = computed(() =>
  getCategoryItemsByIds(questionsStore.question?.categories || [], 'questions')
)

const categorySlug = computed(() => getPageLinkCategorySlug(categories.value))
const pageLink = computed(() => {
  return questionsStore.getQuestionPageLink(id, categorySlug.value)
})

onServerPrefetch(async () => {
  await questionsStore.fetchQuestion(id)
  nuxtApp.runWithContext(() => initMeta())
})

const entity = {
  entity_id: id,
  entity_type: EntityType.Question,
}

const {
  initCountersHelper,
  reactionsCounters,
  onChangeReaction,
  isCountersProcessing,
} = useCountersHelper()

const countersCallbacks = {
  onSendReaction: () => questionsStore.voteForQuestion(id),
  onDeleteReaction: () => questionsStore.deleteVoteForQuestion(id),
}

onMounted(async () => {
  await questionsStore.fetchQuestion(id)
  initMeta()
  await initCountersHelper({
    entity,
    callbacks: countersCallbacks,
    isLoadReactions: true,
  })
})

function scrollToAnswers() {
  route.query.scroll === 'comments' && answersElement.value?.scrollIntoView()
}

const initMeta = () => {
  if (questionsStore.question) {
    const categoriesNames = categories.value.map((item) => item.local)

    useHeadMeta(
      {
        title: questionsStore.question.title,
        description: questionsStore.question.text,
        keywords: categoriesNames.join(', '),
      },
      { canonical: pageLink }
    )
  }
}

const goBackToQuestions = () => {
  const prevRoute = router.options.history.state.back?.toString() || ''

  if (prevRoute.includes('questions') && !prevRoute.includes('item-')) {
    router.back()
  } else {
    router.push(useLocaleRoute()({ name: 'questions' }) as RouteLocationRaw)
  }
}

const onCreateAnswer = () => {
  if (questionsStore.question) {
    questionsStore.question.count_answers += 1
  }
}

const onDeleteAnswer = () => {
  if (questionsStore.question) {
    questionsStore.question.count_answers -= 1
  }
}
</script>
